import React, { useState, useEffect } from 'react';
import { Search, Plus, SortAsc, SortDesc } from 'lucide-react';
// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';

const DialogStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: '0',
    margin: '0'
  },
  dialog: {
    backgroundColor: 'white',
    padding: '0px 20px 10px 20px',
    borderRadius: '8px',
    width: '400px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    flexDirection: 'column',
    gap: '0'
  },
  title: {
    fontSize: '18px',
    textAlign: 'center',
    margin: 0,
    padding: '10px 0',
    color: '#000'
  }
};

const ManageUserAccessDialog = ({
  isOpen,
  onClose,
  corporateAccount,
  projectId,
  productId,
  productName,
  canManageUserAccess,
  canRemoveUserAccess,
  userId,
  accountDescription
}) => {
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUserCorporateAccount, setSelectedUserCorporateAccount] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [accessLevel, setAccessLevel] = useState(1);
  const [successMessage, setSuccessMessage] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Add User Dialog specific states
  const [addUserError, setAddUserError] = useState('');
  const [addUserSuccess, setAddUserSuccess] = useState('');


const [permissionsLoaded, setPermissionsLoaded] = useState(false);


  // New states for dropdown behavior
  const [accountSearchTerm, setAccountSearchTerm] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  // New permission states for managing users in other accounts
  const [crossAccountPermissions, setCrossAccountPermissions] = useState({
    canAddUserToOtherAccounts: false,
    canRemoveUserFromOtherAccounts: false
  });

  const login_token = localStorage.getItem('authToken');

  const styles = {
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '0px',
      backgroundColor: '#fff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      fontSize: '0.875rem',
      margin: '0',
    },
    th: {
      backgroundColor: '#E5E4E2',
      color: '#0064e5',
      padding: '6px 12px',
      textAlign: 'left',
      borderBottom: '2px solid #ddd',
      fontSize: '0.875rem',
      lineHeight: '1',
      height: '32px',
      cursor: 'pointer',
      userSelect: 'none',
      position: 'relative'
    },
    thNonSortable: {
      backgroundColor: '#E5E4E2',
      color: '#0064e5',
      padding: '6px 12px',
      textAlign: 'left',
      borderBottom: '2px solid #ddd',
      fontSize: '0.875rem',
      lineHeight: '1',
      height: '32px'
    },
    td: {
      padding: '12px',
      borderBottom: '1px solid #ddd',
    },
    checkbox: {
      marginRight: '10px',
    },
    deleteButton: {
      backgroundColor: '#dc3545',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      padding: '6px 12px',
      height: '28px',
      fontSize: '0.8rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    disabled: {
      opacity: 0.6
    },
  };

  // Check permissions for managing users in other accounts
// Check permissions for managing users in other accounts
const checkCrossAccountPermissions = async () => {
  console.log('xxx Checking cross-account permissions for user:', userId, 'in corporate account:', corporateAccount, 'for project:', projectId);

  try {
    const response = await fetch(appConfig.getUrl('/get_user_access_level_new'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        user_id: userId,
        project_id: projectId,
        category_header: "Super Admin Functions",
        category_sub_header: []
      }),
    });

    const data = await response.json();

    console.log('Cross-account permissions response JK :', data);

    if (data.status === 'Success') {
      // Create new permissions object
      const newPermissions = {
        canAddUserToOtherAccounts: false,
        canRemoveUserFromOtherAccounts: false
      };

      // Map API response to permission fields
      data.user_actions_list.forEach(item => {
        switch(item.user_action) {
          case 'Add or Update Product Users In Other Accounts':
            newPermissions.canAddUserToOtherAccounts = item.has_access;
            break;
          case 'Remove Product Users In Other Accounts':
            newPermissions.canRemoveUserFromOtherAccounts = item.has_access;
            break;
        }
      });

      setCrossAccountPermissions(newPermissions);
      setPermissionsLoaded(true); // Mark permissions as loaded
      return newPermissions; // Return the permissions for immediate use
    }
  } catch (error) {
    console.error('Error checking cross-account permissions:', error);
  }

  setPermissionsLoaded(true); // Mark as loaded even if there was an error
  return crossAccountPermissions; // Return current state if error
};

// Reset all data
const resetAllData = () => {
  setUsers([]);
  setSelectedUsers(new Set());
  setError('');
  setSuccessMessage('');
  setIsLoading(false);
  setSearchTerm('');
  setFilteredUsers([]);
  setCurrentPage(1);
  setPermissionsLoaded(false); // Add this line
};

  // Sorting functionality
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Apply sorting to users
  const sortedUsers = React.useMemo(() => {
    let sortableUsers = [...filteredUsers];
    if (sortConfig.key) {
      sortableUsers.sort((a, b) => {
        let aVal = a[sortConfig.key] || '';
        let bVal = b[sortConfig.key] || '';

        aVal = aVal.toString().toLowerCase();
        bVal = bVal.toString().toLowerCase();

        if (aVal < bVal) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableUsers;
  }, [filteredUsers, sortConfig]);

  // Pagination
  const totalPages = Math.ceil((sortedUsers?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedUsers = (sortedUsers || []).slice(startIndex, startIndex + itemsPerPage);

  // Effect to fetch users and check permissions when dialog opens
// Modified effect to properly sequence permission check and user fetch
useEffect(() => {
  console.log('ManageUserAccessDialog useEffect - isOpen:', isOpen, 'productId:', productId, 'corporateAccount:', corporateAccount, 'userId:', userId);

  if (isOpen && productId && corporateAccount && userId) {
    resetAllData();

    // First check permissions, then fetch users
    const initializeDialog = async () => {
      const permissions = await checkCrossAccountPermissions();
      // Small delay to ensure state is updated
      setTimeout(() => {
        fetchUsers(permissions);
      }, 100);
    };

    initializeDialog();
  }
}, [isOpen, productId, corporateAccount, userId]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = users.filter(user =>
        (user.user_name && user.user_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        user.user_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.user_account_description && user.user_account_description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        user.user_corporate_account.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
    setCurrentPage(1); // Reset to first page when filtering
  }, [searchTerm, users]);




// Modified fetchUsers to accept permissions parameter for immediate use
const fetchUsers = async (permissions = null) => {
  setIsLoading(true);
  setError('');
  setSuccessMessage('');
  setSelectedUsers(new Set());

  if (!productId) {
    setError('No product selected');
    setIsLoading(false);
    return;
  }

  try {
    // Use permissions parameter if provided, otherwise use state
    const currentPermissions = permissions || crossAccountPermissions;

    // Prepare request body - add user_corporate_account filter if user doesn't have cross-account permissions
    const requestBody = {
      corporate_account: corporateAccount,
      project_id: projectId,
      product_id: productId
    };

    console.log('fetchUsers - currentPermissions:', currentPermissions);

    // If user doesn't have permission to manage other accounts, filter by current account only
    if (!currentPermissions.canAddUserToOtherAccounts) {
      requestBody.user_corporate_account = corporateAccount;
      console.log('Adding user_corporate_account filter:', corporateAccount);
    } else {
      console.log('User has cross-account permissions, not adding filter');
    }

    console.log('fetchUsers - request body:', requestBody);

    const response = await fetch(appConfig.getUrl('/get_product_level_users_list'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify(requestBody)
    });

    const data = await response.json();
    if (data.status === 'Success') {
      setUsers(data.user_list || []);
      setFilteredUsers(data.user_list || []);
    } else if (data.status === 'Failed' && data.status_description === 'No product users found matching the criteria') {
      setUsers([]);
      setFilteredUsers([]);
    } else {
      throw new Error(data.status_description || 'Failed to fetch users');
    }
  } catch (err) {
    setError(err.message);
    console.error('Error fetching users:', err);
  } finally {
    setIsLoading(false);
  }
};




  const handleAddUser = async () => {
    // Check permission before proceeding
    if (!canManageUserAccess) {
      setAddUserError('Insufficient permission to add user access');
      return;
    }

    setIsLoading(true);
    setAddUserError('');
    setAddUserSuccess('');

    // Validate input
    if (!selectedUserId || !selectedUserCorporateAccount) {
      setAddUserError('Please select a user');
      setIsLoading(false);
      return;
    }

    if (!productId) {
      setAddUserError('No product selected');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(appConfig.getUrl('/create_product_level_user_setup'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: projectId,
          product_id: productId,
          user_corporate_account: selectedUserCorporateAccount,
          user_id: selectedUserId,
          access_level: accessLevel
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setAddUserSuccess(data.status_description || 'User access added successfully');

        // Delay to show success message, then refresh and continue
        setTimeout(async () => {
          // First refresh the users list
          await fetchUsers();

          // Set main dialog success message
          setSuccessMessage('User access added successfully');

          // Then close the dialog and clear selections
          setIsAddUserDialogOpen(false);
          setSelectedUserId('');
          setSearchTerm('');
          setAccountSearchTerm('');
          setUserSearchTerm('');
          setAddUserSuccess('');
          setAddUserError('');

          // Clear main success message after user sees it
          setTimeout(() => {
            setSuccessMessage('');
          }, 3000);
        }, 2000);
      } else {
        throw new Error(data.status_description || 'Failed to add user access');
      }
    } catch (err) {
      setAddUserError(err.message);
      console.error('Error adding user access:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateAccessLevel = async (user, newLevel) => {
    // Check permission before proceeding
    if (!canManageUserAccess) {
      setError('Insufficient permission to update access level');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    if (!productId) {
      setError('No product selected');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(appConfig.getUrl('/update_product_level_user_setup'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: projectId,
          product_id: productId,
          user_corporate_account: user.user_corporate_account,
          user_id: user.user_id,
          access_level: newLevel
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setSuccessMessage(data.status_description || `Access level updated for user ${user.user_name || user.user_id}`);

        // Show success message, then refresh
        setTimeout(async () => {
          await fetchUsers();

          // Clear success message after user sees update
          setTimeout(() => {
            setSuccessMessage('');
          }, 5000);
        }, 1000); //verified update
      } else {
        throw new Error(data.status_description || 'Failed to update access level');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error updating access level:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSelected = async () => {
    // Check permission before proceeding
    if (!canRemoveUserAccess) {
      setError('Insufficient permission to remove user access');
      return;
    }

    if (selectedUsers.size === 0) {
      setError('No users selected for removal');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    if (!productId) {
      setError('No product selected');
      setIsLoading(false);
      return;
    }

    // Group users by corporate account for batch deletion
    const usersByAccount = {};

    // Organize selected users by their corporate account
    selectedUsers.forEach(userId => {
      const user = users.find(u => u.user_id === userId);
      if (user) {
        if (!usersByAccount[user.user_corporate_account]) {
          usersByAccount[user.user_corporate_account] = [];
        }
        usersByAccount[user.user_corporate_account].push(user.user_id);
      }
    });

    try {
      const promises = [];

      // Process each corporate account group
      for (const [userCorporateAccount, userIds] of Object.entries(usersByAccount)) {
        promises.push(
          fetch(appConfig.getUrl('/delete_product_level_user_setup'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${login_token}`
            },
            body: JSON.stringify({
              corporate_account: corporateAccount,
              project_id: projectId,
              product_id: productId,
              user_corporate_account: userCorporateAccount,
              user_ids: userIds
            })
          })
        );
      }

      // Wait for all delete operations to complete
      const responses = await Promise.all(promises);
      const results = await Promise.all(responses.map(r => r.json()));

      // Check if any operation failed
      const failures = results.filter(r => r.status !== 'Success');

      if (failures.length > 0) {
        throw new Error(`Failed to remove ${failures.length} users`);
      }

      setSuccessMessage(`Successfully removed ${selectedUsers.size} user(s)`);
      setSelectedUsers(new Set());

      // Show success message, then refresh
      setTimeout(async () => {
        setFilteredUsers([]);
        setUsers([]);

        // Refresh the users list
        await fetchUsers();

        // Clear success message after user sees the result
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      }, 3000); // verified delete users
    } catch (err) {
      setError(err.message);
      console.error('Error removing users:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddUserClick = () => {

    console.log('handleAddUserClick - canManageUserAccess:', canManageUserAccess, 'crossAccountPermissions:', crossAccountPermissions);

    // Check permission before opening dialog
    if (!canManageUserAccess) {
      setError('Insufficient permission to add user access');
      return;
    }

    setSelectedUserId('');
    setSearchTerm('');
    setAccountSearchTerm('');
    setUserSearchTerm('');
    setShowAccountDropdown(false);
    setShowUserDropdown(false);
    setAddUserError('');
    setAddUserSuccess('');
    setIsAddUserDialogOpen(true);

    // If user doesn't have permission to manage other accounts, default to current account
    console.log('crossAccountPermissions:', crossAccountPermissions);

    if (!crossAccountPermissions.canAddUserToOtherAccounts) {
      setSelectedUserCorporateAccount(corporateAccount);
      setAccountSearchTerm(`${accountDescription} (${corporateAccount})`);

      // Fetch users for the current account
      setIsLoadingUsers(true);
      fetch(appConfig.getUrl('/get_user_list'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount
        })
      })
      .then(response => response.json())
      .then(data => {
        if (data.status === 'Success') {
          setAvailableUsers(data.user_list || []);
          setAddUserError('');
        } else {
          setAvailableUsers([]);
          setAddUserError(data.status_description || 'Failed to fetch users');
        }
      })
      .catch(err => {
        console.error('Error fetching users:', err);
        setAvailableUsers([]);
        setAddUserError('Failed to load available users');
      })
      .finally(() => {
        setIsLoadingUsers(false);
      });
    } else {
      // User has permission to manage other accounts, fetch accounts list
      fetch(appConfig.getUrl('/get_account_list'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          user_id: ""
        })
      })
      .then(response => response.json())
      .then(data => {
        if (data.status === 'Success') {
          setAccounts(data.account_list || []);
        } else {
          throw new Error(data.status_description || 'Failed to fetch accounts');
        }
      })
      .catch(err => {
        console.error('Error in setup:', err);
        setAddUserError(err.message);
      });
    }
  };

  const handleAccountSelect = (account) => {
    setSelectedUserCorporateAccount(account.corporate_account);
    setAccountSearchTerm(`${account.account_description} (${account.corporate_account})`);
    setShowAccountDropdown(false);
    setSelectedUserId('');
    setUserSearchTerm('');

    setIsLoadingUsers(true);

    fetch(appConfig.getUrl('/get_user_list'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: account.corporate_account
      })
    })
    .then(response => response.json())
    .then(data => {
      if (data.status === 'Success') {
        setAvailableUsers(data.user_list || []);
        setError('');
      } else {
        setAvailableUsers([]);
        setAddUserError(data.status_description || 'Failed to fetch users');
      }
    })
    .catch(err => {
      console.error('Error fetching users:', err);
      setAvailableUsers([]);
      setAddUserError('Failed to load available users');
    })
    .finally(() => {
      setIsLoadingUsers(false);
    });
  };

  const handleUserSelect = (user) => {
    setSelectedUserId(user.user_id);
    setUserSearchTerm(user.user_name ? `${user.user_name} (${user.user_id})` : user.user_id);
    setShowUserDropdown(false);
  };

  const toggleSelectAll = () => {
    if (selectedUsers.size === paginatedUsers.length) {
      setSelectedUsers(new Set());
    } else {
      const newSelected = new Set(paginatedUsers.map(user => user.user_id));
      setSelectedUsers(newSelected);
    }
  };

  const toggleRowSelection = (userId) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const accessLevelDescriptions = {
    1: 'View Only',
    5: 'Edit'
  };

  // Filter accounts based on search term
  const filteredAccounts = accounts.filter(account =>
    accountSearchTerm === '' ||
    account.account_description.toLowerCase().includes(accountSearchTerm.toLowerCase()) ||
    account.corporate_account.toLowerCase().includes(accountSearchTerm.toLowerCase())
  );

  // Filter available users based on search term
  const filteredAvailableUsers = availableUsers.filter(user =>
    userSearchTerm === '' ||
    (user.user_name && user.user_name.toLowerCase().includes(userSearchTerm.toLowerCase())) ||
    (user.user_id && user.user_id.toLowerCase().includes(userSearchTerm.toLowerCase()))
  );

  // Add User Dialog Content
  const renderAddUserDialog = () => {
    if (!isAddUserDialogOpen) return null;

    return (
      <div style={DialogStyles.overlay}>
        <div style={{...DialogStyles.dialog, width: '600px'}}>
          <h2 style={{ ...DialogStyles.title, backgroundColor: '#CCCCFF', height: 40 }}>Add User Access</h2>

          <div className="mb-6 p-4 border border-gray-300 rounded-md">
            <h3 className="text-base font-semibold mb-3">Adding user access to: <strong>{productName || 'No product selected'}</strong></h3>
          </div>

          {/* Corporate Account Selection - Only show if user has cross-account permissions */}
          {crossAccountPermissions.canAddUserToOtherAccounts && (
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Select Corporate Account: <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Start typing to search accounts..."
                  value={accountSearchTerm}
                  onChange={(e) => {
                    setAccountSearchTerm(e.target.value);
                    setShowAccountDropdown(e.target.value.length > 0);
                  }}
                  onFocus={() => setShowAccountDropdown(accountSearchTerm.length > 0)}
                  onBlur={() => setTimeout(() => setShowAccountDropdown(false), 200)}
                  className="w-full p-2 border rounded"
                  disabled={isLoading}
                  required
                />

                {showAccountDropdown && (
                  <div className="absolute z-10 w-full max-h-60 overflow-y-auto border rounded mt-1 bg-white shadow-lg">
                    {accounts.length === 0 ? (
                      <div className="p-2 text-gray-500">Loading accounts...</div>
                    ) : filteredAccounts.length === 0 ? (
                      <div className="p-2 text-gray-500">No accounts found</div>
                    ) : (
                      filteredAccounts.map(account => (
                        <div
                          key={account.corporate_account}
                          className="p-2 cursor-pointer hover:bg-blue-50"
                          onClick={() => handleAccountSelect(account)}
                        >
                          {account.account_description} ({account.corporate_account})
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* User Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Select User: <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="Start typing to search users..."
                value={userSearchTerm}
                onChange={(e) => {
                  setUserSearchTerm(e.target.value);
                  setShowUserDropdown(e.target.value.length > 0);
                }}
                onFocus={() => setShowUserDropdown(userSearchTerm.length > 0)}
                onBlur={() => setTimeout(() => setShowUserDropdown(false), 200)}
                className="w-full p-2 border rounded"
                disabled={isLoadingUsers || isLoading || !selectedUserCorporateAccount}
                required
              />

              {showUserDropdown && (
                <div className="absolute z-10 w-full max-h-60 overflow-y-auto border rounded mt-1 bg-white shadow-lg">
                  {isLoadingUsers ? (
                    <div className="p-2 text-gray-500">Loading users...</div>
                  ) : !selectedUserCorporateAccount ? (
                    <div className="p-2 text-gray-500">Select a corporate account first</div>
                  ) : availableUsers.length === 0 ? (
                    <div className="p-2 text-gray-500">No users available</div>
                  ) : filteredAvailableUsers.length === 0 ? (
                    <div className="p-2 text-gray-500">No users found</div>
                  ) : (
                    filteredAvailableUsers.map(user => (
                      <div
                        key={user.user_id}
                        className="p-2 cursor-pointer hover:bg-blue-50"
                        onClick={() => handleUserSelect(user)}
                      >
                        {user.user_name ? `${user.user_name} (${user.user_id})` : user.user_id}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Access Level Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Access Level: <span className="text-red-500">*</span>
            </label>
            <select
              value={accessLevel}
              onChange={(e) => setAccessLevel(Number(e.target.value))}
              className="w-full p-2 border rounded"
              disabled={isLoading}
            >
              {Object.entries(accessLevelDescriptions).map(([level, description]) => (
                <option key={level} value={level}>
                  {level} - {description}
                </option>
              ))}
            </select>
          </div>

          {/* Error and Success messages using BusinessTeams format - specific to Add User Dialog */}
          {addUserError && (
            <div className="mb-4 p-2 rounded bg-red-50 text-red-500">
              {addUserError}
            </div>
          )}

          {addUserSuccess && (
            <div className="mb-4 p-2 rounded bg-green-50 text-green-700">
              {addUserSuccess}
            </div>
          )}

          <div className="flex justify-center gap-4 mt-6">
            <button
              onClick={() => setIsAddUserDialogOpen(false)}
              style={{
                backgroundColor: '#FFFDD0',
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
              disabled={isLoading}
            >
              Close
            </button>
            <button
              onClick={handleAddUser}
              style={{
                padding: '8px 16px',
                backgroundColor: '#C8F4DE',
                color: 'black',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
              disabled={isLoading || !selectedUserId || !selectedUserCorporateAccount}
            >
              {isLoading ? 'Adding...' : 'Add User'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div style={DialogStyles.overlay}>
        <div style={{
          ...DialogStyles.dialog,
          width: '1500px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          padding: '0',
          gap: '0',
          margin: '0'
        }}>

          {/* Header */}
          <div className="px-6 pt-0 pb-0">
            <div className="flex items-center mb-4 mt-0 justify-between bg-[#CCCCFF] p-3 rounded">
              {/* Search bar */}
              <div className="flex items-center">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      fetchUsers();
                    }
                  }}
                  placeholder="Search users..."
                  className="p-2 pr-10 border rounded w-full"
                />
                <button
                    onClick={() => fetchUsers()}
                  className="ml-2 p-2 rounded bg-blue-500 hover:bg-blue-600 text-white"
                >
                  <Search size={18} />
                </button>
              </div>

              <h1 style={{
                flex: 1,
                textAlign: 'center',
                fontSize: '18px',
                fontWeight: 'bold',
                color: 'black',
                margin: 0
              }}>
                Manage User Access - {productName || 'No product selected'}
              </h1>

              {/* Add New User button with permission check and tooltip */}
              <div className="group relative">
                <button
                  className={`flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 ${!canManageUserAccess ? 'opacity-50' : ''}`}
                  onClick={handleAddUserClick}
                  disabled={isLoading || !canManageUserAccess}
                >
                  <Plus size={18} className="mr-2" />
                  Add New User
                </button>

                {/* Tooltip for insufficient permission */}
                {!canManageUserAccess && (
                  <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                    Insufficient permission
                    <div className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Error/Success Messages using BusinessTeams format */}
          <div className="px-6">
            {error && (
              <div className="mb-4 p-2 rounded bg-red-50 text-red-500">
                {error}
              </div>
            )}

            {successMessage && (
              <div className="mb-4 p-2 rounded bg-green-50 text-green-700">
                {successMessage}
              </div>
            )}

            {users.length === 0 && !isLoading && !error && (
              <div className="mb-4 p-2 rounded bg-yellow-50 text-yellow-700">
                No users have access to this product
              </div>
            )}
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto px-6">
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.thNonSortable}>
                    <input
                      type="checkbox"
                      checked={paginatedUsers.length > 0 && selectedUsers.size === paginatedUsers.length}
                      onChange={toggleSelectAll}
                      disabled={paginatedUsers.length === 0}
                      style={styles.checkbox}
                    />
                    Select All
                  </th>
                  <th
                    style={styles.th}
                    onClick={() => handleSort('user_id')}
                    className="select-none hover:bg-gray-200 transition-colors"
                  >
                    <div className="flex items-center">
                      <span style={{ textDecoration: 'underline' }}>User ID</span>
                      <span className="ml-1">
                        {sortConfig.key === 'user_id' ? (
                          sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                        ) : null}
                      </span>
                    </div>
                  </th>
                  <th
                    style={styles.th}
                    onClick={() => handleSort('user_name')}
                    className="select-none hover:bg-gray-200 transition-colors"
                  >
                    <div className="flex items-center">
                      <span style={{ textDecoration: 'underline' }}>User Name</span>
                      <span className="ml-1">
                        {sortConfig.key === 'user_name' ? (
                          sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                        ) : null}
                      </span>
                    </div>
                  </th>
                  <th
                    style={styles.th}
                    onClick={() => handleSort('user_account_description')}
                    className="select-none hover:bg-gray-200 transition-colors"
                  >
                    <div className="flex items-center">
                      <span style={{ textDecoration: 'underline' }}>User Account</span>
                      <span className="ml-1">
                        {sortConfig.key === 'user_account_description' ? (
                          sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                        ) : null}
                      </span>
                    </div>
                  </th>
                  <th
                    style={styles.th}
                    onClick={() => handleSort('access_level')}
                    className="select-none hover:bg-gray-200 transition-colors"
                  >
                    <div className="flex items-center">
                      <span style={{ textDecoration: 'underline' }}>Access Level</span>
                      <span className="ml-1">
                        {sortConfig.key === 'access_level' ? (
                          sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                        ) : null}
                      </span>
                    </div>
                  </th>
                  <th
                    style={styles.th}
                    onClick={() => handleSort('created_date')}
                    className="select-none hover:bg-gray-200 transition-colors"
                  >
                    <div className="flex items-center">
                      <span style={{ textDecoration: 'underline' }}>Created Date</span>
                      <span className="ml-1">
                        {sortConfig.key === 'created_date' ? (
                          sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                        ) : null}
                      </span>
                    </div>
                  </th>
                  <th
                    style={styles.th}
                    onClick={() => handleSort('updated_date')}
                    className="select-none hover:bg-gray-200 transition-colors"
                  >
                    <div className="flex items-center">
                      <span style={{ textDecoration: 'underline' }}>Updated Date</span>
                      <span className="ml-1">
                        {sortConfig.key === 'updated_date' ? (
                          sortConfig.direction === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                        ) : null}
                      </span>
                    </div>
                  </th>
                </tr>
              </thead>

              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-8">Loading...</td>
                  </tr>
                ) : paginatedUsers.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-8 text-gray-500">
                      No users found. Add a new user or adjust your search.
                    </td>
                  </tr>
                ) : (
                  paginatedUsers.map((user, index) => (
                    <tr
                      key={`${user.user_corporate_account}-${user.user_id}-${index}`}
                      className="border-t hover:bg-gray-50"
                      style={{
                        backgroundColor: selectedUsers.has(user.user_id) ? '#DEEFF5' : 'transparent'
                      }}
                    >
                      <td style={styles.td}>
                        <input
                          type="checkbox"
                          checked={selectedUsers.has(user.user_id)}
                          onChange={() => toggleRowSelection(user.user_id)}
                          style={styles.checkbox}
                        />
                      </td>
                      <td style={styles.td}>{user.user_id}</td>
                      <td style={styles.td}>{user.user_name || 'N/A'}</td>
                      <td style={styles.td}>
                        <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                          {user.user_account_description || user.user_corporate_account}
                        </span>
                      </td>
                      <td style={styles.td}>
                        {/* Access Level dropdown with permission check and tooltip */}
                        <div className="group relative">
                          <select
                            value={user.access_level}
                            onChange={(e) => {
                              if (canManageUserAccess) {
                                handleUpdateAccessLevel(user, Number(e.target.value));
                              }
                            }}
                            className={`p-1 border rounded ${!canManageUserAccess ? 'opacity-50 ' : ''}`}
                            disabled={!canManageUserAccess}
                          >
                            {Object.entries(accessLevelDescriptions).map(([level, description]) => (
                              <option key={level} value={level}>
                                {level} - {description}
                              </option>
                            ))}
                          </select>

                          {/* Tooltip for insufficient permission */}
                          {!canManageUserAccess && (
                            <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                              Insufficient permission
                              <div className="absolute right-full top-1/2 transform -translate-y-1/2 w-0 h-0 border-t-4 border-b-4 border-r-4 border-transparent border-r-gray-200"></div>
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={styles.td}>{new Date(user.created_date).toLocaleDateString()}</td>
                      <td style={styles.td}>{new Date(user.updated_date).toLocaleDateString()}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination controls */}
          {!isLoading && sortedUsers.length > 0 && (
            <div className="mx-6 mt-4 flex justify-between items-center bg-white p-4 rounded-lg shadow">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>Total Users: {sortedUsers.length}</span>
                <span>| Showing {sortedUsers.length > 0 ? startIndex + 1 : 0} - {Math.min(startIndex + itemsPerPage, sortedUsers.length)} of {sortedUsers.length}</span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span className="text-sm text-blue-600">Page {currentPage} of {Math.max(1, totalPages)}</span>
                <button
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-50"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages || totalPages === 0}
                  aria-label="Next page"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>

              <div className="flex items-center">
                <span className="text-sm text-gray-600 mr-2">Items per page:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border rounded p-1 text-sm"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          )}

          {/* Footer with action buttons */}
          <div className="p-6 pt-4 border-t">
            <div className="flex justify-between items-center">
              <div className="flex gap-2">
                {/* Remove Selected button with permission check and tooltip */}
                <div className="group relative">
                  <button
                    style={{
                      ...styles.deleteButton,
                      padding: '8px 16px',
                      ...(isLoading || selectedUsers.size === 0 || !canRemoveUserAccess ? styles.disabled : {})
                    }}
                    onClick={handleDeleteSelected}
                    disabled={isLoading || selectedUsers.size === 0 || !canRemoveUserAccess}
                  >
                    {isLoading ? 'Removing...' : `Remove Selected (${selectedUsers.size})`}
                  </button>

                  {/* Tooltip for insufficient permission */}
                  {!canRemoveUserAccess && selectedUsers.size > 0 && (
                    <div className="absolute left-0 top-full mt-2 px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                      Insufficient permission
                      <div className="absolute bottom-full left-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  {selectedUsers.size > 0 ? `${selectedUsers.size} selected` : ''}
                </span>
                <button
                  style={{
                    backgroundColor: '#FFFDD0',
                    padding: '8px 16px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                  onClick={onClose}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Add User Dialog */}
        {renderAddUserDialog()}
      </div>
    </div>
  );
};

export default ManageUserAccessDialog;